import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { useApi } from '@/hooks/useApi';
import { formatDateShort } from '@/lib/utils';
import { AffiliateLeaderboardContent } from '@/pages/affiliate/AffiliateLeaderboard';
import IndividualLeaderboard from '@/pages/individual/IndividualLeaderboard';
import MemberLeaderboard from '@/pages/member/MemberLeaderboard';
import { useEffectiveRole } from '@/stores/authStore';
import type { LeaderboardEntry } from '@ergshoot/shared';
import { useQuery } from '@tanstack/react-query';
import { Building2, Loader2, Medal, Trophy } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

interface Workout {
  id: string;
  name: string;
  scheduled_date?: string;
}

interface WorkoutsResponse {
  items: Workout[];
  total: number;
}

interface LeaderboardResponse {
  leaderboard: LeaderboardEntry[];
  total_entries: number;
}

/**
 * Admin Leaderboard View - Shows scores across all affiliates
 */
function AdminLeaderboardView() {
  const { api } = useApi();
  const currentRole = useEffectiveRole();
  const isAdmin = currentRole === 'hq_admin';
  const [selectedWorkoutId, setSelectedWorkoutId] = useState<string>('');

  // Fetch workouts for selection
  const { data: workoutsData, isLoading: workoutsLoading } = useQuery({
    queryKey: ['admin-workouts-for-leaderboard'],
    queryFn: async () => {
      const response = await api.get<WorkoutsResponse>('/programming/workouts?limit=50');
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  const workoutOptions = useMemo(() => {
    const workouts = workoutsData?.items || [];
    return workouts
      .filter((w) => w.scheduled_date)
      .map((w) => ({
        value: w.id,
        label: `${w.name} (${formatDateShort(w.scheduled_date)})`,
      }));
  }, [workoutsData?.items]);

  // Get today's workout by default
  const todayStr = new Date().toISOString().split('T')[0];
  const effectiveWorkoutId = useMemo(() => {
    if (selectedWorkoutId) return selectedWorkoutId;
    const todayWorkout = workoutsData?.items?.find(
      (w) => w.scheduled_date?.split('T')[0] === todayStr
    );
    return todayWorkout?.id || workoutsData?.items?.[0]?.id || '';
  }, [selectedWorkoutId, workoutsData?.items, todayStr]);

  // Fetch scores for selected workout
  const { data: leaderboardData, isLoading: scoresLoading } = useQuery({
    queryKey: ['admin-leaderboard-scores', effectiveWorkoutId],
    queryFn: async () => {
      if (!effectiveWorkoutId) return { leaderboard: [], total_entries: 0 };
      const response = await api.get<LeaderboardResponse>(
        `/scores/leaderboard/${effectiveWorkoutId}?limit=50`
      );
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
    enabled: !!effectiveWorkoutId,
    refetchInterval: 30000,
  });

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1:
        return 'text-yellow-500';
      case 2:
        return 'text-gray-400';
      case 3:
        return 'text-amber-600';
      default:
        return 'text-muted-foreground';
    }
  };

  const getRankIcon = (rank: number) => {
    if (rank <= 3) {
      return <Trophy className={`h-5 w-5 ${getRankColor(rank)}`} />;
    }
    return <span className="w-5 text-center">{rank}</span>;
  };

  const rankedScores = leaderboardData?.leaderboard || [];

  if (workoutsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gradient-gold">Leaderboard</h1>
          <p className="mt-1 text-muted-foreground">View all available scores.</p>
        </div>
        {isAdmin && (
          <Button asChild variant="outline">
            <Link to="/admin/scores">
              <Medal className="h-4 w-4 mr-2" />
              Manage Scores
            </Link>
          </Button>
        )}
      </div>

      {/* Workout Selector */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <label htmlFor="workout-select" className="text-sm font-medium">
              Select Workout:
            </label>
            <div className="flex-1 max-w-sm">
              <Select
                id="workout-select"
                options={[{ value: '', label: 'Select a track...' }, ...workoutOptions]}
                value={selectedWorkoutId || effectiveWorkoutId}
                onChange={(e) => setSelectedWorkoutId(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leaderboard */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Medal className="h-5 w-5 text-primary" />
            Rankings
            <Badge variant="outline" className="ml-2">
              All Affiliates
            </Badge>
          </CardTitle>
          <CardDescription>Scores from all registered members</CardDescription>
        </CardHeader>
        <CardContent>
          {scoresLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : rankedScores.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Medal className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">No scores yet</h3>
              <p className="text-muted-foreground max-w-md">
                {effectiveWorkoutId
                  ? 'No members have logged scores for this workout yet.'
                  : 'Select a track to view the leaderboard.'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {rankedScores.map((entry) => (
                <div
                  key={entry.score_id}
                  className={`flex items-center gap-4 p-3 rounded-lg ${
                    entry.rank <= 3 ? 'bg-primary/5' : 'bg-muted/50'
                  }`}
                >
                  <div className="flex items-center justify-center w-8">
                    {getRankIcon(entry.rank)}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{entry.user_name}</p>
                    {entry.affiliate_name && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {entry.affiliate_name}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <span
                      className={`font-mono font-semibold ${
                        entry.rank === 1 ? 'text-primary' : ''
                      }`}
                    >
                      {entry.score_display}
                    </span>
                    {entry.division === 'rx' && (
                      <Badge variant="secondary" className="ml-2 text-xs">
                        RX
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Shared Leaderboard page that renders the appropriate component based on user role.
 *
 * - Affiliate Owner/Coach: Uses AffiliateLeaderboard (gym-wide)
 * - Member: Uses MemberLeaderboard (gym-wide)
 * - Individual: Uses IndividualLeaderboard (global)
 * - HQ Admin: Uses AdminLeaderboardView (cross-affiliate)
 * - Agent: Shows "not applicable" message
 */
export default function Leaderboard() {
  const effectiveRole = useEffectiveRole();

  // HQ Admin sees cross-affiliate leaderboard
  if (effectiveRole === 'hq_admin') {
    return <AdminLeaderboardView />;
  }

  // Affiliate owners and coaches see gym leaderboard
  if (effectiveRole === 'affiliate_owner' || effectiveRole === 'affiliate_coach') {
    return <AffiliateLeaderboardContent />;
  }

  // Members see their gym leaderboard
  if (effectiveRole === 'member') {
    return <MemberLeaderboard />;
  }

  // Individuals see global leaderboard
  if (effectiveRole === 'individual') {
    return <IndividualLeaderboard />;
  }

  // Agents see all available scores (read-only, cross-affiliate view)
  if (effectiveRole === 'agent') {
    return <AdminLeaderboardView />;
  }

  // Fallback
  return (
    <>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gradient-gold">Leaderboard</h1>
        <p className="mt-1 text-muted-foreground">View all available scores.</p>
      </div>

      <Card>
        <CardContent className="py-12 text-center">
          <div className="rounded-full bg-muted p-4 mx-auto w-fit mb-4">
            <Medal className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Leaderboard</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            Leaderboards are available to gym members and individual subscribers.
          </p>
        </CardContent>
      </Card>
    </>
  );
}
